import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageLoader, PageActions } from '@app/components';
import { generateID, genericErrorString, hasPermission } from '@app/helpers';
import { ManageSystem } from '@app/store/constants';
import { useParams } from 'react-router';
import { PolicyForm as EditPolicyForm } from '../policy';
import {
    addAction,
    addCondition,
    addDetailsBreadcrumb,
    addElseAction,
    addMainRule,
    addMainRuleAction,
    addMainRuleCondition,
    addMainRuleElseAction,
    addRule,
    removeDetailsBreadcrumb,
    setExceptionHandler
} from '@app/store/actions';
import { basicService, policiesAuthService } from '@app/services';
import {
    IActionData,
    IAdditionalRuleConfiguration,
    IConditionData,
    IElseActionData,
    IGlobalState,
    IPolicyAuth,
    IRule,
    IRuleConfiguration,
    PolicyID,
    RuleIDType
} from '@app/models';
import { AxiosError, AxiosResponse } from 'axios';
import { useTypedTranslation } from '@app/hooks';
import { prepareData } from '../policy/methods';
import { Link, Typography } from '@material-ui/core';

const AuthenticationDetailsPage = () => {
    const loadingData: boolean = useSelector((state: IGlobalState) => state.basic.loadingData);
    const mainPolicyData: IRule = useSelector((state: IGlobalState) => state.policiesAuth.mainRule);
    const dispatch = useDispatch();
    const { id: policyId } = useParams<{ id: PolicyID }>();
    const { translation } = useTypedTranslation();

    const [isNoElseAction, setIsNoElseAction] = useState<boolean>(!mainPolicyData.elseActions?.length);
    const [disabledSaving, isSavingDisabled] = useState<boolean>(true);
    const [defaultPolicy, setDefaultPolicy] = useState<boolean>(false);
    const [policyName, setPolicyName] = useState<string>('');
    const [rulesLoaded, areRulesLoaded] = useState<boolean>(false);
    const [submitInProgess, isSubmitInProgress] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorElseActionMessage, setErrorElseActionMessage] = useState(isNoElseAction && rulesLoaded ? translation('ELSE_IS_REQUIRED') : '');
    const [isOldPolicy, setIsOldPolicy] = useState(false);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        policiesAuthService.getPolicyDetails(policyId, handleError, handleSuccess);
        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        if (rulesLoaded) {
            setIsNoElseAction(!mainPolicyData.elseActions?.length);
            setErrorElseActionMessage(!mainPolicyData.elseActions?.length ? translation('ELSE_IS_REQUIRED') : '');
        }
    }, [mainPolicyData.elseActions, rulesLoaded]);

    const handleSubmit = () => {
        isSubmitInProgress(true);
        policiesAuthService.updatePolicy(policyId, prepareData(mainPolicyData, policyName, defaultPolicy), handleError, handleUpdateSuccess);
    };

    const handleValidation = (isValid: boolean) => {
        isValid ? isSavingDisabled(false) : isSavingDisabled(true);
    };

    const handleDefaultPolicy = (isDefault: boolean) => {
        setDefaultPolicy(isDefault);
    };

    const handlePolicyName = (name: string) => {
        setPolicyName(name);
    };

    const handleSuccess = (response: AxiosResponse<IPolicyAuth>) => {
        dispatch(addDetailsBreadcrumb(response.data.DisplayName));
        setPolicyName(response.data.DisplayName);
        setDefaultPolicy(response.data.IsDefault);
        let configuration = JSON.parse(response.data.Configuration);
        let mainRule = configuration[0];

        if (configuration.length > 1) {
            setIsOldPolicy(true);
            mainRule.elseRules = configuration.splice(1);
        }
        prepareEditData(mainRule);
    };

    const handleUpdateSuccess = () => {
        basicService.redirect('/policies/auth');
    };

    const handleError = (error: AxiosError<any>) => {
        isSubmitInProgress(false);
        setErrorMessage(genericErrorString(error));
    };

    const prepareMainRule = (mainRule: IRuleConfiguration) => {
        dispatch(
            addMainRule({
                operator: mainRule.operator,
                conditions: [],
                actions: [],
                elseActions: [],
                elseRules: []
            })
        );

        mainRule.conditions.forEach((condition: IConditionData) => {
            dispatch(
                addMainRuleCondition({
                    filter: condition.filter,
                    property: condition.property,
                    type: condition.type
                })
            );
        });

        mainRule.actions.forEach((action: IActionData) => {
            dispatch(
                addMainRuleAction({
                    Configuration: action.Configuration,
                    Type: action.Type
                })
            );
        });

        mainRule.elseActions.forEach((elseAction: IElseActionData) => {
            dispatch(
                addMainRuleElseAction({
                    Configuration: elseAction.Configuration,
                    Type: elseAction.Type
                })
            );
        });
    };

    const prepareRule = (rule: IAdditionalRuleConfiguration) => {
        const tempRuleID: RuleIDType = generateID(20);

        dispatch(
            addRule({
                operator: rule.operator,
                conditions: [],
                actions: [],
                id: tempRuleID
            })
        );

        rule.conditions.forEach((condition: IConditionData) => {
            dispatch(
                addCondition(
                    {
                        filter: condition.filter,
                        property: condition.property,
                        type: condition.type
                    },
                    tempRuleID
                )
            );
        });

        rule.actions.forEach((action: IActionData) => {
            dispatch(
                addAction(
                    {
                        Configuration: action.Configuration,
                        Type: action.Type
                    },
                    tempRuleID
                )
            );
        });

        rule.elseActions?.forEach((elseAction: IElseActionData) => {
            dispatch(
                addElseAction(tempRuleID, {
                    Configuration: elseAction.Configuration,
                    Type: elseAction.Type
                })
            );
        });
    };

    const prepareEditData = (mainRule: IRuleConfiguration): void => {
        prepareMainRule(mainRule);

        mainRule.elseRules?.forEach((rule: IAdditionalRuleConfiguration) => {
            prepareRule(rule);
        });

        areRulesLoaded(true);
    };

    return (
        <>
            {loadingData && <PageLoader />}
            {!loadingData && hasPermission([ManageSystem]) && (
                <div className='content detailsPage'>
                    <EditPolicyForm
                        handleValidation={handleValidation}
                        isDefault={handleDefaultPolicy}
                        policyName={handlePolicyName}
                        isEdit={true}
                        rulesLoaded={rulesLoaded}
                        data={{ name: policyName, default: defaultPolicy }}
                    />

                    {(errorMessage || errorElseActionMessage || isOldPolicy) && (
                        <div className='policyForm__actions'>
                            <Typography variant='body1' color='error'>
                                {errorMessage || errorElseActionMessage}
                                {isOldPolicy ? (
                                    <span>
                                        {translation('OLD_POLICY')}{' '}
                                        <Link
                                            href='https://helpdesk.kaseya.com/hc/en-gb/articles/4407409722385-Working-with-Passly-Authentication-Policies'
                                            rel='noopener noreferrer'
                                            target='_blank'
                                            underline='always'
                                            className='error__text'
                                        >
                                            <i>{translation('RECOMMENDATIONS_LINK')}</i>
                                        </Link>
                                    </span>
                                ) : (
                                    ''
                                )}
                            </Typography>
                        </div>
                    )}
                    <PageActions
                        primaryBtn={translation('SAVE_CHANGES')}
                        onClickPrimary={handleSubmit}
                        disabled={disabledSaving || submitInProgess}
                        isLoading={submitInProgess}
                    />
                </div>
            )}
        </>
    );
};

export default AuthenticationDetailsPage;
